import type { Asset } from '../types'
import { API_BASE, authHeaders } from './apiAuth'

/**
 * Naming a picture, wherever it is kept
 * -------------------------------------------------------------
 * The library is company photography. A project's files are what was dropped
 * on its board or made there. More stores will follow. Everything that takes a
 * picture — a skill, the canvas, the hand-off to Miro — wants a picture, and
 * none of them should have to know which store it came out of.
 *
 * So a picture is a pair, written as one string wherever a list of ids already
 * existed: `library:8f2c…`, `project:19ab…`. A bare id means the library,
 * which is what everything written before this looks like.
 *
 * This mirrors workers/api/src/refs.ts. Two copies, because the two are
 * separate builds — and the format is four lines of string handling, which is
 * a smaller thing to keep in step than a shared package would be to set up.
 */

export type ImageStore = 'library' | 'project'

export interface ImageRef {
  store: ImageStore
  id: string
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const encodeRef = (ref: ImageRef): string => `${ref.store}:${ref.id}`

export function decodeRef(value: unknown): ImageRef | null {
  // Anything at all may arrive here — a row from the server, a dropped
  // payload, a pasted string. A decoder that assumes it was handed a string is
  // a decoder that throws on the one input it exists to reject.
  if (typeof value !== 'string') return null
  const colon = value.indexOf(':')
  if (colon < 0) return UUID.test(value) ? { store: 'library', id: value } : null
  const store = value.slice(0, colon)
  const id = value.slice(colon + 1)
  if (store !== 'library' && store !== 'project') return null
  return UUID.test(id) ? { store, id } : null
}

/**
 * Names into pairs, discarding whatever is not a name.
 *
 * Takes `unknown` rather than `string[]` on purpose: every caller is a trust
 * boundary — a server response, a dropped payload, a pasted string — and a
 * decoder that can only be called once the data has already been asserted to
 * be the right shape is a decoder that is never there when it is needed. It
 * was typed as `string[]` and the one caller that mattered simply did not call
 * it, because the type above it already claimed the work had been done.
 */
export const decodeRefs = (values: unknown): ImageRef[] =>
  Array.isArray(values)
    ? values.map(decodeRef).filter((ref): ref is ImageRef => ref !== null)
    : []

/** A library image, named. The common case, and worth not spelling out. */
/**
 * What a picture is called while it is being dragged.
 *
 * A type of our own rather than `text/plain`: a board should not accept a
 * sentence dropped on it as though it were a picture, and a picture dragged
 * out of here into another application should leave that application alone.
 */
export const REF_DRAG_TYPE = 'application/x-wonderworks-refs'

export const libraryRef = (id: string): ImageRef => ({ store: 'library', id })

/**
 * Turn names into pictures with links that load.
 *
 * Always through the Worker, from either surface. The Miro panel cannot sign
 * for itself, and a project's files are readable only by people on that
 * project — a question storage cannot answer and the Worker can.
 */
export async function resolveImages(refs: ImageRef[]): Promise<(Asset & { store: ImageStore })[]> {
  if (refs.length === 0) return []
  const response = await fetch(`${API_BASE}/api/images/resolve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ refs }),
  })
  if (!response.ok) throw new Error(`Those images could not be read (HTTP ${response.status})`)
  return (await response.json()) as (Asset & { store: ImageStore })[]
}
